import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import FieldTypes from '../../../enums/field-types';
import { accessData, can, route } from '../../../services/utils';
import Spinner from '../spinner';
import i18n from 'i18next';
import css from './static-table.module.css';
import { PartialAbility } from '../../../router/abilityContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import toFixed from 'accounting-js/lib/toFixed.js';
import { TableColumnGroup } from './table';

export interface StaticTableColumn<T> {
  accessor: keyof T | (keyof T)[];
  aggregateAccessor?: string;
  header: string | React.ReactNode;
  translatable?: any;
  translationPrefix?: string;
  dataType?: FieldTypes;
  roundDecimals?: number;
  className?: string;
}

export function loadingStaticTable() {
  return (
    <div className={css['loading']}>
      <div>{i18n.t('Loading') as string}</div>
      <Spinner visible={true} size="lg" />
    </div>
  );
}

export default function StaticTable<T>({
  data,
  keyAccessor,
  noDataMessage = 'No entries found',
  columns,
  columnGroups = null,
  aggregateLabels,
  viewRoute,
  rowClassSetter = null,
  footer = null,
  viewRecordAbility = null,
  fixedHeight = false,
  visible,
}: {
  data?: {
    rows: T[];
    aggregates?: any;
  };
  visible?: boolean;
  keyAccessor?: keyof T | ((t: T) => any);
  noDataMessage?: string;
  columns: StaticTableColumn<T>[];
  columnGroups?: TableColumnGroup<T>[];
  showAggregates?: boolean;
  aggregateLabels?: { [key: string]: string };
  viewRoute?: string | ((row: T) => string);
  rowClassSetter?: (row: T) => string;
  footer?: ReactElement;
  viewRecordAbility?: PartialAbility;
  fixedHeight?: boolean;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [authorizedViewRoute, setAuthorizedViewRoute] = useState<
    string | ((row: T) => string) | null
  >(null);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  useEffect(() => {
    if (viewRecordAbility && can(farmUser, viewRecordAbility)) {
      setAuthorizedViewRoute(() => viewRoute);
    }
  }, [farmUser, viewRecordAbility, viewRoute]);

  function key(value: T) {
    if (
      typeof keyAccessor === 'string' ||
      typeof keyAccessor === 'number' ||
      typeof keyAccessor === 'symbol'
    ) {
      return value[keyAccessor];
    } else {
      return keyAccessor(value);
    }
  }

  function thead() {
    function tableColumnGroups() {
      if (columnGroups === null) {
        return <></>; // not used by default
      } else {
        let tableColumnGroups = [];
        for (let index = 0; index < columns.length; index++) {
          // manual iteration because we jump over indices sometimes
          let currentColumn = columns[index];
          let columnGroupForColumn =
            columnGroups.find(
              (columnGroup) =>
                columnGroup.startsAtAccessor === currentColumn.accessor
            ) || null;

          if (columnGroupForColumn !== null) {
            tableColumnGroups.push(
              <th
                scope="colgroup"
                key={'specified-thead' + index.toString()}
                colSpan={columnGroupForColumn.colspan}
                className={css['col-group'].concat(
                  ' ',
                  columnGroupForColumn.className
                    ? columnGroupForColumn.className
                    : ''
                )}
              >
                {columnGroupForColumn.header}
              </th>
            );
            index += columnGroupForColumn.colspan - 1;
          } else {
            tableColumnGroups.push(
              <th key={'default-thead' + index.toString()}></th>
            );
          }
        }
        return <tr>{tableColumnGroups}</tr>;
      }
    }

    return (
      <thead>
        {tableColumnGroups()}
        <tr>
          {columns.map((column) => (
            <th scope="col" key={column.accessor.toString()}>
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  function tbody() {
    function renderTdContent(
      data: any,
      dataType: FieldTypes,
      column: StaticTableColumn<T>
    ) {
      let td: any = data;

      switch (true) {
        case dataType === FieldTypes.DECIMAL:
          if (column.roundDecimals) {
            td = parseFloat(toFixed(data, column.roundDecimals));
          }
          break;
        case !data && dataType !== FieldTypes.DECIMAL:
          td = '';
          break;
        case dataType === FieldTypes.BOOLEAN:
        case typeof data === 'boolean':
          td = data ? (
            <FontAwesomeIcon icon={faCheck} className="text-accent" />
          ) : (
            ''
          );
          break;
        case dataType === FieldTypes.JSON_AGG:
          td = (JSON.parse(td) as string[]).map((value, index) => (
            <div key={index}>{value}</div>
          ));
          break;
        case typeof data === 'object' && Array.isArray(data):
          td = data.join(', ');
          break;
        case RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/).test(data):
        case RegExp(/^\d{4}-\d{2}-\d{2}$/).test(data):
          td = moment(data).format('DD.MM.YYYY');
          break;
      }

      return td;
    }

    function open(row: T) {
      if (authorizedViewRoute) {
        if (typeof authorizedViewRoute === 'function') {
          history.push(authorizedViewRoute(row));
        } else if (typeof authorizedViewRoute === 'string') {
          history.push(
            route(authorizedViewRoute, {
              [keyAccessor.toString()]: key(row).toString(),
            })
          );
        }
      }
    }

    function row(row: T, index: number) {
      return (
        <tr
          className={`table-row ${authorizedViewRoute ? css['viewable'] : ''} ${
            rowClassSetter ? rowClassSetter(row) : ''
          }`}
          key={key(row)?.toString() || index}
        >
          {columns.map((column) => (
            <td
              className={column.className}
              key={key(row).toString().concat('-', column.accessor.toString())}
              onClick={() => open(row)}
            >
              {renderTdContent(
                accessData(row, column),
                column.dataType,
                column
              )}
            </td>
          ))}
        </tr>
      );
    }

    function rows() {
      let elements = [];
      // half indexes are hidden rows for expanded data
      for (let index = 0.0; index <= data.rows.length - 0.5; index += 0.5) {
        if (index === Math.floor(index)) {
          elements.push(row(data.rows[index], index));
        }
      }
      return elements;
    }

    if (!data || data.rows.length === 0) {
      return (
        <tbody className={css['without-data']}>
          <tr>
            <td colSpan={columns.length}>{t(noDataMessage)}</td>
          </tr>
        </tbody>
      );
    } else {
      return <tbody className={css['with-data']}>{rows()}</tbody>;
    }
  }

  function tfoot() {
    function aggregates() {
      if (!data?.aggregates) {
        return null;
      }
      const aggs = data.aggregates;
      return (
        <>
          {Object.keys(aggs).map((aggKey, index) => (
            <tr
              className={`${css['aggregate']} ${aggKey}`}
              key={aggKey.concat(index.toString())}
            >
              <td
                className={css['aggregate-label']}
                key={aggKey.concat(index.toString(), 'label')}
              >
                {aggregateLabels && aggregateLabels[aggKey]}
              </td>
              {columns.map(
                (column, index) =>
                  index !== 0 && (
                    <td
                      className={css['aggregate-val']}
                      key={aggKey.concat(
                        index.toString(),
                        column.aggregateAccessor
                      )}
                    >
                      {aggs[aggKey][column.aggregateAccessor]}
                    </td>
                  )
              )}
            </tr>
          ))}
        </>
      );
    }

    return <tfoot>{aggregates()}</tfoot>;
  }

  return (
    <div className={css['container']}>
      <div
        className={`${css['wrapper']} ${visible ? css['visible'] : ''} ${
          fixedHeight ? 'max-h-[62vh]' : ''
        }`}
      >
        <table className={`${css['table']}`}>
          {thead()}
          {tbody()}
          {tfoot()}
        </table>
        {footer}
      </div>
    </div>
  );
}
